/**
 * BAB Rueckfragen — the "Queries" tab inside the side panel.
 *
 * Three views in one pane, so the user never loses the anchor they opened it from:
 *   list   — the queries of this line / account / booking, bubbled up like comments
 *   form   — raise a new one (subject, question, assignees, requested date, priority)
 *   detail — the thread: replies and events interleaved, plus the status buttons
 *            the acting user is actually allowed to press
 *
 * The status buttons mirror the server's state machine (query-service.canTransition).
 * They are a convenience, not the control — every transition is re-decided server-side,
 * and a refusal comes back as a 400 with the reason, which is shown verbatim.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Loader2, Plus, ArrowLeft, ArrowUpRight, CalendarClock, AlertTriangle,
    CheckCircle2, XCircle, Send, RotateCcw, Lock, Trash2,
    MessageSquare, ChevronDown, ChevronRight, History,
    Paperclip, FileText, Image as ImageIcon, X,
} from 'lucide-react';
import { babReportApi } from '../../services/babReportApi';
import { useAuth } from '../../contexts/AuthContext';
import { MultiSearchableSelect } from '../ui/MultiSearchableSelect';
import { describeTransactionRef } from '../../utils/babTransactionRef';
import { refPeriodLabel } from '../../utils/babFormat';
import type {
    BabQuery, BabQueryDetail, BabQueryStatus, BabQueryPriority, BabQueryMessage,
    BabAssignableUser, BabCommentTarget, BabQueryHistoryItem, BabQueryAttachment,
} from '../../types/babReport';

const MAX_SUBJECT = 200;
const MAX_QUESTION = 2000;
const MAX_REPLY = 4000;

/** Mirrors ATTACHMENT_MIME_ALLOWLIST / MAX_ATTACHMENT_BYTES in query-service.ts. */
const ATTACH_ACCEPT = 'application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg';
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const STATUS_CHIP: Record<BabQueryStatus, string> = {
    Open: 'bg-amber-100 text-amber-800',
    InProgress: 'bg-blue-100 text-blue-800',
    Answered: 'bg-emerald-100 text-emerald-800',
    Closed: 'bg-gray-200 text-gray-600',
    Rejected: 'bg-rose-100 text-rose-700',
};

const STATUS_LABEL: Record<BabQueryStatus, string> = {
    Open: 'Open',
    InProgress: 'In progress',
    Answered: 'Answered',
    Closed: 'Closed',
    Rejected: 'Rejected',
};

const LEVEL_CHIP: Record<string, string> = {
    line: 'bg-gray-100 text-gray-600',
    account: 'bg-indigo-100 text-indigo-700',
    booking: 'bg-amber-100 text-amber-800',
};

function formatDate(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function formatDateTime(value: string): string {
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A due date only counts as overdue while somebody still has to act on it. */
export function isOverdue(q: { dueDate: string | null; status: BabQueryStatus }): boolean {
    if (!q.dueDate) return false;
    if (q.status === 'Closed' || q.status === 'Rejected') return false;
    const due = new Date(q.dueDate);
    due.setHours(23, 59, 59, 999);
    return due.getTime() < Date.now();
}

/** The place a query hangs on, spelled out — same vocabulary as the comment cards. */
function scopeLabel(q: BabQuery): string {
    if (q.anchorLevel === 'booking') {
        return describeTransactionRef(q.transactionRef) ?? `Booking on account ${q.accountCode ?? '—'}`;
    }
    if (q.anchorLevel === 'account') {
        const company = q.sourceCompanyCode ? ` · ${q.sourceCompanyCode.toUpperCase()}` : '';
        return `${q.accountCode}${q.accountName ? ` — ${q.accountName}` : ''}${company}`;
    }
    const parts: string[] = [];
    if (q.lineCode) parts.push(`${q.lineCode}${q.lineName ? ` ${q.lineName}` : ''}`);
    parts.push(q.babCompanyCode ? q.babCompanyCode.toUpperCase() : 'All reports');
    parts.push(q.buCode || 'All BUs');
    return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

/**
 * An iPhone hands a photo through with an EMPTY `file.type`, so the browser-reported
 * MIME alone silently breaks mobile uploads. Fall back to the extension — the same
 * two-sided guard the visit-report uploader needs. The server resolves it the same way
 * and has the final say.
 */
function resolveMimeType(file: File): string {
    if (file.type) return file.type === 'image/jpg' ? 'image/jpeg' : file.type;
    switch (file.name.toLowerCase().split('.').pop() ?? '') {
        case 'pdf': return 'application/pdf';
        case 'png': return 'image/png';
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        case 'heic': return 'image/heic';
        case 'heif': return 'image/heif';
        default: return '';
    }
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('The file could not be read.'));
        reader.readAsDataURL(file);
    });
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface AttachmentsProps {
    items: BabQueryAttachment[];
    canAttach: boolean;
    /** Uploader or admin — the server re-decides. */
    canRemove: (a: BabQueryAttachment) => boolean;
    onPick: (file: File) => void;
    onOpen: (a: BabQueryAttachment) => void;
    onRemove: (a: BabQueryAttachment) => void;
    busy: boolean;
    /** "Attach a file" under the question, "Attach" under a reply. */
    label: string;
}

/**
 * The chips plus the picker for one slot (the question, or one reply).
 *
 * Opening costs a request: the SAS is minted per click and lives ~10 minutes, so
 * nothing here holds a URL. The blank tab is opened synchronously BEFORE the await —
 * a popup blocker kills a window opened from a resolved promise.
 */
function Attachments({
    items, canAttach, canRemove, onPick, onOpen, onRemove, busy, label,
}: AttachmentsProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    if (items.length === 0 && !canAttach) return null;

    return (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {items.map(a => (
                <span
                    key={a.attachmentId}
                    className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white pl-1.5 pr-1 py-0.5 text-[11px] text-gray-700"
                >
                    <button
                        type="button"
                        onClick={() => onOpen(a)}
                        title={`${a.fileName} — ${formatBytes(a.sizeBytes)}, attached by ${a.uploadedBy}`}
                        className="inline-flex items-center gap-1 hover:underline max-w-[190px]"
                    >
                        {a.contentType === 'application/pdf'
                            ? <FileText className="h-3 w-3 flex-shrink-0 text-rose-600" />
                            : <ImageIcon className="h-3 w-3 flex-shrink-0 text-indigo-600" />}
                        <span className="truncate">{a.fileName}</span>
                        <span className="text-gray-400 flex-shrink-0">{formatBytes(a.sizeBytes)}</span>
                    </button>
                    {canRemove(a) && (
                        <button
                            type="button"
                            onClick={() => onRemove(a)}
                            disabled={busy}
                            title="Remove this attachment"
                            className="text-gray-400 hover:text-rose-600 disabled:opacity-50"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </span>
            ))}

            {canAttach && (
                <>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ATTACH_ACCEPT}
                        className="hidden"
                        onChange={e => {
                            const file = e.target.files?.[0];
                            e.target.value = '';        // same file twice must re-fire
                            if (file) onPick(file);
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={busy}
                        className="inline-flex items-center gap-1 rounded border border-dashed border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
                        {label}
                    </button>
                </>
            )}
        </div>
    );
}

/** Human sentence for one thread event. */
function describeEvent(m: BabQueryMessage): string {
    const p = (m.eventPayload ?? {}) as Record<string, unknown>;
    switch (m.eventType) {
        case 'Created': return 'raised this query';
        case 'StatusChanged': return `changed the status from ${p.from} to ${p.to}${p.reason ? ` — ${p.reason}` : ''}`;
        case 'Reopened': return 'reopened the query';
        case 'Assigned': return `added ${(p.added as string[] ?? []).join(', ')}`;
        case 'Unassigned': return `removed ${(p.removed as string[] ?? []).join(', ')}`;
        case 'DueDateChanged': return `moved the requested date to ${p.to ? formatDate(String(p.to)) : '—'}`;
        case 'CommentLinked': return 'turned the answer into a comment';
        default: return m.eventType ?? 'changed something';
    }
}

interface Props {
    target: BabCommentTarget | null;
    /** All queries of the period; the pane narrows them to the target itself. */
    queries: BabQuery[];
    loading: boolean;
    canEdit: boolean;
    periodYear: number;
    periodMonth: number;
    filters: { babCompanyCode?: string; buCode?: string; sourceCompany?: string };
    /** Reload the period's queries in the page after any write. */
    onChanged: () => void;
    /** Jump to the place a query hangs on. */
    onNavigate: (q: BabQuery) => void;
    /** Deep link: open this query straight away. */
    focusQueryId?: number | null;
    onFocusHandled?: () => void;
    /**
     * Show the comment an answer was turned into — the panel switches to the Comments
     * tab and highlights it. Without this the user has to trust that the write worked.
     */
    onShowComment?: (commentId: number) => void;
}

export function BabQueryPane({
    target, queries, loading, canEdit,
    periodYear, periodMonth, filters, onChanged, onNavigate,
    focusQueryId, onFocusHandled, onShowComment,
}: Props) {
    const { user } = useAuth();
    const myEmail = (user?.email ?? '').toLowerCase();

    const [view, setView] = useState<'list' | 'form' | 'detail'>('list');
    const [openId, setOpenId] = useState<number | null>(null);
    const [detail, setDetail] = useState<BabQueryDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ---- new-query form state ----
    const [subject, setSubject] = useState('');
    const [question, setQuestion] = useState('');
    const [assignees, setAssignees] = useState<string[]>([]);
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState<BabQueryPriority>('Normal');
    const [people, setPeople] = useState<BabAssignableUser[]>([]);
    const [peopleLoading, setPeopleLoading] = useState(false);

    const [reply, setReply] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [showReject, setShowReject] = useState(false);
    const [attachBusy, setAttachBusy] = useState(false);

    // ---- "Earlier queries on this position" ----
    const [historyOpen, setHistoryOpen] = useState(false);
    const [history, setHistory] = useState<BabQueryHistoryItem[] | null>(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Reset to the list whenever the anchor changes — a detail view left over from a
    // different line would be a lie about what the panel is showing.
    useEffect(() => {
        setView('list'); setOpenId(null); setDetail(null); setError(null);
        setHistoryOpen(false); setHistory(null);
    }, [target]);

    const loadDetail = useCallback(async (queryId: number) => {
        setDetailLoading(true);
        setError(null);
        try {
            const d = await babReportApi.getQuery(queryId);
            setDetail(d);
            setOpenId(queryId);
            setView('detail');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load the query');
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // Deep link from a notification mail.
    useEffect(() => {
        if (focusQueryId) {
            void loadDetail(focusQueryId);
            onFocusHandled?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusQueryId]);

    /**
     * What belongs to the opened level. Identical bubbling to the comment panel: a line
     * shows everything below it, so the badge in the grid is never a dead end.
     */
    const visible = useMemo(() => {
        if (!target) return queries;
        const lineCodes = new Set([target.lineCode, ...(target.rolledUpLineCodes ?? [])]);
        const sameLine = queries.filter(q => !!q.lineCode && lineCodes.has(q.lineCode));
        if (target.level === 'line') return sameLine;
        const sameAccount = sameLine.filter(q =>
            q.accountCode === target.accountCode && q.sourceCompanyCode === target.companyCode);
        if (target.level === 'account') return sameAccount;
        return sameAccount.filter(q => q.transactionRef === target.transactionRef);
    }, [queries, target]);

    // The picker needs the anchor's source company so it can tell who may be made
    // responsible for a BOOKING query. Loaded when the form opens, not on every render.
    useEffect(() => {
        if (view !== 'form') return;
        setPeopleLoading(true);
        babReportApi.getAssignableUsers(target?.companyCode)
            .then(setPeople)
            .catch(() => setPeople([]))
            .finally(() => setPeopleLoading(false));
    }, [view, target?.companyCode]);

    const isBookingAnchor = target?.level === 'booking';
    const peopleOptions = useMemo(() => people.map(p => ({
        id: p.email,
        // The reason is in the label because MultiSearchableSelect has no disabled state —
        // and a silent omission would read as "that person does not exist".
        name: isBookingAnchor && !p.canOpenBookingDetail
            ? `${p.displayName} — no booking access`
            : p.displayName,
    })), [people, isBookingAnchor]);

    const blockedSelected = useMemo(() => {
        if (!isBookingAnchor) return [];
        return assignees.filter(e =>
            people.find(p => p.email === e && !p.canOpenBookingDetail));
    }, [assignees, people, isBookingAnchor]);

    const resetForm = () => {
        setSubject(''); setQuestion(''); setAssignees([]); setDueDate('');
        setPriority('Normal'); setError(null);
    };

    const submitNew = async () => {
        if (!target || !subject.trim() || !question.trim() || assignees.length === 0) return;
        setBusy(true);
        setError(null);
        try {
            await babReportApi.createQuery(periodYear, periodMonth, target, {
                subject: subject.trim(),
                questionText: question.trim(),
                assignees,
                dueDate: dueDate || null,
                priority,
            }, filters);
            resetForm();
            setView('list');
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to raise the query');
        } finally {
            setBusy(false);
        }
    };

    const submitReply = async () => {
        if (!openId || !reply.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await babReportApi.replyToQuery(openId, reply.trim());
            setReply('');
            await loadDetail(openId);
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to send the reply');
        } finally {
            setBusy(false);
        }
    };

    const withdraw = async () => {
        if (!openId) return;
        if (!window.confirm('Withdraw this query? It disappears for everyone; the thread is kept.')) return;
        setBusy(true);
        setError(null);
        try {
            await babReportApi.withdrawQuery(openId);
            setView('list');
            setDetail(null);
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to withdraw the query');
        } finally {
            setBusy(false);
        }
    };

    /**
     * Keep one answer as a permanent comment at the same position. Offered only once the
     * query is settled (Answered or Closed) — promoting an interim reply would put a
     * half-answer where every future reader looks.
     */
    const keepAsComment = async (messageId: number) => {
        if (!openId) return;
        setBusy(true);
        setError(null);
        try {
            const res = await babReportApi.queryAnswerToComment(openId, messageId);
            await loadDetail(openId);
            onChanged();
            onShowComment?.(res.commentId);
        } catch (err) {
            // A 409 arrives here too, and its message names the existing comment —
            // more useful than "failed".
            setError(err instanceof Error ? err.message : 'Failed to keep the answer as a comment');
        } finally {
            setBusy(false);
        }
    };

    // -----------------------------------------------------------------------
    // Attachments
    // -----------------------------------------------------------------------

    /**
     * `messageId === null` hangs the file on the question, otherwise on that reply.
     * The two pre-checks below only save the user a pointless 10 MB round trip —
     * the type allowlist and the size limit are decided by the server, and its
     * sentence is what gets shown.
     */
    const uploadAttachment = async (file: File, messageId: number | null) => {
        if (!openId) return;
        setError(null);

        if (file.size > MAX_ATTACHMENT_BYTES) {
            setError(`"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is 10 MB.`);
            return;
        }

        setAttachBusy(true);
        try {
            await babReportApi.addQueryAttachment(openId, {
                fileName: file.name,
                contentType: resolveMimeType(file),
                fileBase64: await fileToBase64(file),
                messageId,
            });
            await loadDetail(openId);
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to attach the file');
        } finally {
            setAttachBusy(false);
        }
    };

    /**
     * The tab is opened SYNCHRONOUSLY, before the request — a window opened after an
     * await is a popup a blocker eats. The SAS lives ~10 minutes and is fetched per
     * click, so nothing here keeps a URL around.
     */
    const openAttachment = async (a: BabQueryAttachment) => {
        const tab = window.open('', '_blank');
        if (tab) tab.opener = null;
        try {
            const { url } = await babReportApi.getQueryAttachmentUrl(a.attachmentId);
            if (tab) tab.location.href = url;
            else window.location.assign(url);
        } catch (err) {
            tab?.close();
            setError(err instanceof Error ? err.message : 'Failed to open the attachment');
        }
    };

    const removeAttachment = async (a: BabQueryAttachment) => {
        if (!openId) return;
        if (!window.confirm(`Remove "${a.fileName}"? The thread keeps the fact that it was attached.`)) return;
        setAttachBusy(true);
        setError(null);
        try {
            await babReportApi.removeQueryAttachment(a.attachmentId);
            await loadDetail(openId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove the attachment');
        } finally {
            setAttachBusy(false);
        }
    };

    /** Lazy — the section is collapsed by default, so most opens cost no request. */
    const toggleHistory = async () => {
        const next = !historyOpen;
        setHistoryOpen(next);
        if (!next || history !== null || !target) return;
        setHistoryLoading(true);
        try {
            setHistory(await babReportApi.getQueryHistory({
                lineCode: target.lineCode,
                periodYear,
                periodMonth,
                sourceCompany: target.level === 'line' ? null : target.companyCode ?? null,
                accountCode: target.level === 'line' ? null : target.accountCode ?? null,
            }));
        } catch {
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const changeStatus = async (status: BabQueryStatus, reason?: string) => {
        if (!openId) return;
        setBusy(true);
        setError(null);
        try {
            await babReportApi.setQueryStatus(openId, status, reason);
            setShowReject(false);
            setRejectReason('');
            await loadDetail(openId);
            onChanged();
        } catch (err) {
            // The server's own sentence — "Only the person who raised the query can
            // close it" is more useful than a generic failure.
            setError(err instanceof Error ? err.message : 'Failed to change the status');
        } finally {
            setBusy(false);
        }
    };

    // -----------------------------------------------------------------------
    // detail view
    // -----------------------------------------------------------------------
    if (view === 'detail') {
        const canReply = canEdit && detail
            && detail.status !== 'Closed' && detail.status !== 'Rejected';
        const role = detail?.role;
        const canWork = !!role && (role.isAssignee || role.isAdmin);
        const canClose = !!role && (role.isAuthor || role.isAdmin);
        // Same right as closing: the asker decides that the answer resolved the question,
        // so the asker decides it is worth writing down for good.
        const canKeepAsComment = canEdit && canClose && !!detail
            && !detail.linkedCommentId
            && (detail.status === 'Answered' || detail.status === 'Closed');
        // Attaching is part of the conversation, like replying — the same three roles,
        // and only while the query is still live. The server decides again.
        const canAttach = !!canReply && !!role && (role.isAuthor || role.isAssignee || role.isAdmin);
        const attachments = detail?.attachments ?? [];
        // Uploader or admin. The server re-decides and refuses with its own sentence;
        // this only keeps an X from appearing on a file the user cannot take down.
        const canRemoveAttachment = (a: BabQueryAttachment) =>
            canEdit && (!!role?.isAdmin || a.uploadedBy.toLowerCase() === myEmail);

        return (
            <div className="flex flex-col flex-1 min-h-0">
                <div className="px-5 pt-3 pb-2 border-b border-gray-100">
                    <button
                        onClick={() => { setView('list'); setDetail(null); setError(null); }}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to the list
                    </button>
                </div>

                {error && <p className="mx-5 mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}

                <div className="flex-1 overflow-auto p-5 space-y-4">
                    {detailLoading || !detail ? (
                        <p className="text-sm text-gray-500">Loading…</p>
                    ) : (
                        <>
                            <div>
                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                    <span className={`text-[11px] font-medium rounded px-1.5 py-0.5 ${STATUS_CHIP[detail.status]}`}>
                                        {STATUS_LABEL[detail.status]}
                                    </span>
                                    <span className={`text-[11px] font-medium rounded px-1.5 py-0.5 uppercase tracking-wide ${LEVEL_CHIP[detail.anchorLevel]}`}>
                                        {detail.anchorLevel}
                                    </span>
                                    {detail.priority !== 'Normal' && (
                                        <span className="text-[11px] rounded px-1.5 py-0.5 bg-gray-100 text-gray-700">
                                            {detail.priority} priority
                                        </span>
                                    )}
                                    {isOverdue(detail) && (
                                        <span className="inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 bg-red-100 text-red-700">
                                            <AlertTriangle className="h-3 w-3" /> Overdue
                                        </span>
                                    )}
                                </div>

                                <h4 className="text-sm font-semibold text-gray-900">{detail.subject}</h4>

                                <button
                                    type="button"
                                    onClick={() => onNavigate(detail)}
                                    className="mt-1 inline-flex items-start gap-1 text-left text-[11px] text-gray-500 hover:underline"
                                    title="Open the place this query belongs to"
                                >
                                    {scopeLabel(detail)}
                                    <ArrowUpRight className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                </button>

                                <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{detail.questionText}</p>

                                <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs text-gray-600">
                                    <dt className="text-gray-400">Asked by</dt>
                                    <dd>{detail.createdBy} · {formatDate(detail.createdAt)}</dd>
                                    <dt className="text-gray-400">Responsible</dt>
                                    <dd>{detail.assignees.join(', ') || '—'}</dd>
                                    <dt className="text-gray-400">Requested by</dt>
                                    <dd className={isOverdue(detail) ? 'text-red-600 font-medium' : ''}>
                                        {detail.dueDate ? formatDate(detail.dueDate) : '—'}
                                    </dd>
                                    {detail.closureReason && (
                                        <>
                                            <dt className="text-gray-400">Reason</dt>
                                            <dd>{detail.closureReason}</dd>
                                        </>
                                    )}
                                </dl>

                                {/* Files hanging on the QUESTION itself — the screenshot of
                                    the figure that prompted it. */}
                                <Attachments
                                    items={attachments.filter(a => a.messageId === null)}
                                    canAttach={canAttach}
                                    canRemove={canRemoveAttachment}
                                    onPick={file => uploadAttachment(file, null)}
                                    onOpen={openAttachment}
                                    onRemove={removeAttachment}
                                    busy={attachBusy}
                                    label="Attach a file"
                                />

                                {detail.linkedCommentId && (
                                    <button
                                        type="button"
                                        onClick={() => onShowComment?.(detail.linkedCommentId!)}
                                        className="mt-3 inline-flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 hover:bg-blue-100"
                                    >
                                        <MessageSquare className="h-3.5 w-3.5" />
                                        The answer is kept as a comment — show it
                                    </button>
                                )}
                            </div>

                            <div className="border-t border-gray-100 pt-3 space-y-2.5">
                                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">History</p>
                                {detail.messages.length === 0 && (
                                    <p className="text-xs text-gray-400">Nothing yet.</p>
                                )}
                                {detail.messages.map(m => m.kind === 'Reply' ? (
                                    <div key={m.messageId} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{m.text}</p>
                                        <div className="mt-1.5 flex items-center justify-between gap-2">
                                            <p className="text-xs text-gray-500">
                                                {m.createdBy} · {formatDateTime(m.createdAt)}
                                            </p>
                                            {canKeepAsComment && (
                                                <button
                                                    type="button"
                                                    onClick={() => keepAsComment(m.messageId)}
                                                    disabled={busy}
                                                    title="Save this answer as a comment on the same position, visible to everyone"
                                                    className="inline-flex items-center gap-1 text-xs text-gray-600 border border-gray-300 rounded px-2 py-0.5 hover:bg-white disabled:opacity-50 flex-shrink-0"
                                                >
                                                    <MessageSquare className="h-3 w-3" /> Keep as comment
                                                </button>
                                            )}
                                        </div>

                                        {/* The evidence for THIS answer, not for the thread. */}
                                        <Attachments
                                            items={attachments.filter(a => a.messageId === m.messageId)}
                                            canAttach={canAttach}
                                            canRemove={canRemoveAttachment}
                                            onPick={file => uploadAttachment(file, m.messageId)}
                                            onOpen={openAttachment}
                                            onRemove={removeAttachment}
                                            busy={attachBusy}
                                            label="Attach"
                                        />
                                    </div>
                                ) : (
                                    <p key={m.messageId} className="text-xs text-gray-400 pl-1">
                                        <span className="text-gray-500">{m.createdBy}</span> {describeEvent(m)}
                                        <span className="text-gray-300"> · {formatDateTime(m.createdAt)}</span>
                                    </p>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {detail && canEdit && (
                    <div className="px-5 py-3 border-t border-gray-200 space-y-2">
                        {canReply && (
                            <>
                                <textarea
                                    value={reply}
                                    onChange={e => setReply(e.target.value)}
                                    maxLength={MAX_REPLY}
                                    rows={2}
                                    placeholder="Write a reply…"
                                    className="w-full text-sm border rounded-md px-2 py-1.5"
                                />
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-400">{reply.length}/{MAX_REPLY}</span>
                                    <button
                                        onClick={submitReply}
                                        disabled={busy || !reply.trim()}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50"
                                    >
                                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                        Reply
                                    </button>
                                </div>
                            </>
                        )}

                        {showReject ? (
                            <div className="space-y-1.5">
                                <textarea
                                    value={rejectReason}
                                    onChange={e => setRejectReason(e.target.value)}
                                    rows={2}
                                    maxLength={1000}
                                    placeholder="Why is this query not relevant? (required)"
                                    className="w-full text-sm border rounded-md px-2 py-1.5"
                                />
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => changeStatus('Rejected', rejectReason.trim())}
                                        disabled={busy || !rejectReason.trim()}
                                        className="px-3 py-1.5 text-sm rounded-md bg-rose-600 text-white disabled:opacity-50"
                                    >
                                        Reject
                                    </button>
                                    <button onClick={() => setShowReject(false)} className="px-3 py-1.5 text-sm rounded-md border">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {canWork && detail.status === 'Open' && (
                                    <button onClick={() => changeStatus('InProgress')} disabled={busy}
                                        className="px-2.5 py-1.5 text-xs rounded-md border border-gray-300 hover:bg-gray-50">
                                        Start working
                                    </button>
                                )}
                                {canWork && (detail.status === 'Open' || detail.status === 'InProgress') && (
                                    <button
                                        onClick={() => changeStatus('Answered')}
                                        disabled={busy || detail.replyCount === 0}
                                        title={detail.replyCount === 0 ? 'Write the answer first' : undefined}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-emerald-600 text-white disabled:opacity-50"
                                    >
                                        <CheckCircle2 className="h-3.5 w-3.5" /> Mark as answered
                                    </button>
                                )}
                                {canClose && detail.status === 'Answered' && (
                                    <button onClick={() => changeStatus('Closed')} disabled={busy}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-gray-700 text-white disabled:opacity-50">
                                        <CheckCircle2 className="h-3.5 w-3.5" /> Close
                                    </button>
                                )}
                                {canWork && detail.status !== 'Closed' && detail.status !== 'Rejected' && (
                                    <button onClick={() => setShowReject(true)} disabled={busy}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50">
                                        <XCircle className="h-3.5 w-3.5" /> Not relevant
                                    </button>
                                )}
                                {canClose && (detail.status === 'Closed' || detail.status === 'Rejected') && (
                                    <button onClick={() => changeStatus('Open')} disabled={busy}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-gray-300 hover:bg-gray-50">
                                        <RotateCcw className="h-3.5 w-3.5" /> Reopen
                                    </button>
                                )}
                                {/* Taking back a question you should not have asked. Confirmed,
                                    because it removes it from everyone else's list too. */}
                                {canClose && (
                                    <button onClick={withdraw} disabled={busy}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md text-gray-500 hover:bg-gray-100 ml-auto">
                                        <Trash2 className="h-3.5 w-3.5" /> Withdraw
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // new-query form
    // -----------------------------------------------------------------------
    if (view === 'form') {
        return (
            <div className="flex flex-col flex-1 min-h-0">
                <div className="px-5 pt-3 pb-2 border-b border-gray-100">
                    <button
                        onClick={() => { setView('list'); resetForm(); }}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Cancel
                    </button>
                </div>

                {error && <p className="mx-5 mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}

                <div className="flex-1 overflow-auto p-5 space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
                        <input
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            maxLength={MAX_SUBJECT}
                            placeholder="e.g. Personnel expenses 18 % over budget"
                            className="w-full text-sm border rounded-md px-2 py-1.5"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Question</label>
                        <textarea
                            value={question}
                            onChange={e => setQuestion(e.target.value)}
                            maxLength={MAX_QUESTION}
                            rows={4}
                            placeholder="What would you like explained?"
                            className="w-full text-sm border rounded-md px-2 py-1.5"
                        />
                        <span className="text-xs text-gray-400">{question.length}/{MAX_QUESTION}</span>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Responsible</label>
                        {peopleLoading ? (
                            <p className="text-xs text-gray-400">Loading people…</p>
                        ) : (
                            <MultiSearchableSelect
                                options={peopleOptions}
                                values={assignees}
                                onChange={setAssignees}
                                placeholder="Who should answer this?"
                                maxChips={3}
                            />
                        )}
                        {isBookingAnchor && (
                            <p className="mt-1 inline-flex items-start gap-1 text-xs text-gray-500">
                                <Lock className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                This query is about an individual posting, so it can only go to people
                                who may open booking detail of {(target?.companyCode ?? '').toUpperCase()}.
                            </p>
                        )}
                        {blockedSelected.length > 0 && (
                            <p className="mt-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                                No booking access for {(target?.companyCode ?? '').toUpperCase()}: {blockedSelected.join(', ')}.
                                Remove them, or raise the query on the account instead.
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Requested by</label>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={e => setDueDate(e.target.value)}
                                className="w-full text-sm border rounded-md px-2 py-1.5"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                            <select
                                value={priority}
                                onChange={e => setPriority(e.target.value as BabQueryPriority)}
                                className="w-full text-sm border rounded-md px-2 py-1.5"
                            >
                                <option value="Low">Low</option>
                                <option value="Normal">Normal</option>
                                <option value="High">High</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={submitNew}
                        disabled={busy || !subject.trim() || !question.trim()
                            || assignees.length === 0 || blockedSelected.length > 0}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50"
                    >
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Send query
                    </button>
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // list
    // -----------------------------------------------------------------------
    return (
        <div className="flex flex-col flex-1 min-h-0">
            {error && <p className="mx-5 mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}

            <div className="flex-1 overflow-auto p-5 space-y-3">
                {loading ? (
                    <p className="text-gray-500 text-sm">Loading queries…</p>
                ) : visible.length === 0 ? (
                    <p className="text-gray-500 text-sm">
                        No queries {target ? 'on this position' : 'this period'} yet.
                    </p>
                ) : (
                    visible.map(q => (
                        <button
                            key={q.queryId}
                            onClick={() => loadDetail(q.queryId)}
                            className="w-full text-left rounded-lg border border-gray-200 bg-white hover:bg-gray-50 p-3"
                        >
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className={`text-[11px] font-medium rounded px-1.5 py-0.5 ${STATUS_CHIP[q.status]}`}>
                                    {STATUS_LABEL[q.status]}
                                </span>
                                <span className={`text-[11px] font-medium rounded px-1.5 py-0.5 uppercase tracking-wide ${LEVEL_CHIP[q.anchorLevel]}`}>
                                    {q.anchorLevel}
                                </span>
                                {/* The month the query is about — a list can span several. */}
                                <span
                                    className="text-[11px] font-medium rounded px-1.5 py-0.5 bg-slate-100 text-slate-600"
                                    title="The period this query refers to"
                                >
                                    {refPeriodLabel(q)}
                                </span>
                                {isOverdue(q) && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 bg-red-100 text-red-700">
                                        <AlertTriangle className="h-3 w-3" /> Overdue
                                    </span>
                                )}
                            </div>

                            <p className="text-sm font-medium text-gray-900">{q.subject}</p>
                            <p className="text-[11px] text-gray-500 mt-0.5 break-words">{scopeLabel(q)}</p>

                            <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                                <span>{q.assignees.join(', ') || 'nobody'}</span>
                                {q.dueDate && (
                                    <span className={`inline-flex items-center gap-1 ${isOverdue(q) ? 'text-red-600 font-medium' : ''}`}>
                                        <CalendarClock className="h-3 w-3" /> {formatDate(q.dueDate)}
                                    </span>
                                )}
                                {q.replyCount > 0 && <span>{q.replyCount} repl{q.replyCount === 1 ? 'y' : 'ies'}</span>}
                            </div>
                        </button>
                    ))
                )}

                {/* Earlier periods on the same position. Collapsed by default: the point is
                    that a recurring question is visible at all, not that it competes with
                    what is open now. */}
                {target && (
                    <div className="border-t border-gray-100 pt-3">
                        <button
                            type="button"
                            onClick={toggleHistory}
                            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800"
                        >
                            {historyOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            <History className="h-3.5 w-3.5" />
                            Earlier queries on this {target.level}
                            {history !== null && <span className="text-gray-400">({history.length})</span>}
                        </button>

                        {historyOpen && (
                            <div className="mt-2 space-y-1.5">
                                {historyLoading ? (
                                    <p className="text-xs text-gray-400">Loading…</p>
                                ) : !history?.length ? (
                                    <p className="text-xs text-gray-400">
                                        Nothing was asked here in an earlier period.
                                    </p>
                                ) : (
                                    history.map(h => (
                                        <button
                                            key={h.queryId}
                                            onClick={() => loadDetail(h.queryId)}
                                            className="w-full text-left rounded border border-gray-200 bg-white hover:bg-gray-50 px-2.5 py-1.5"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-[11px] font-medium text-gray-500 tabular-nums">
                                                    {h.periodYear}-{String(h.periodMonth).padStart(2, '0')}
                                                </span>
                                                <span className={`text-[11px] font-medium rounded px-1.5 py-0.5 ${STATUS_CHIP[h.status]}`}>
                                                    {STATUS_LABEL[h.status]}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-800 mt-0.5">{h.subject}</p>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200">
                {target && canEdit ? (
                    <button
                        onClick={() => setView('form')}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-md bg-blue-600 text-white"
                    >
                        <Plus className="h-4 w-4" /> Ask a question about this {target.level}
                    </button>
                ) : (
                    <p className="text-xs text-gray-400">
                        {target
                            ? 'You need edit permission on this page to raise a query.'
                            : 'Open a line, account or booking to raise a query.'}
                    </p>
                )}
            </div>
        </div>
    );
}

export { STATUS_CHIP, STATUS_LABEL };
